import {
  ContentState,
  FormActions,
  FormField,
  Select,
  StatusPill,
  TextArea,
  TextInput,
} from "@whitehaven/ui";
import type {
  AdminLeadEngagementCursor,
  AdminLeadNote,
  AdminLeadNoteCreateRequest,
  AdminLeadTag,
  AdminLeadTagMutationRequest,
  AdminLeadTask,
  AdminLeadTaskMutationRequest,
  AdminLeadTaskReminder,
  AdminLeadTaskReminderMutationRequest,
  IdempotencyKey,
  LeadId,
} from "@whitehaven/contracts";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import type {
  AdminLeadEngagementGateways,
  AdminLeadEngagementReadContext,
} from "./lead-engagement-gateway";
import { ADMIN_LEAD_ENGAGEMENT_PAGE_SIZE } from "./lead-engagement-gateway";
import {
  applyConfirmedAdminLeadNoteCreation,
  applyConfirmedAdminLeadReminderMutation,
  applyConfirmedAdminLeadTagMutation,
  applyConfirmedAdminLeadTaskMutation,
  crmTaskStatusLabels,
  formatHawaiiEngagementDate,
  hawaiiLocalDateTimeToCanonicalUtc,
  isAdminLeadNotePageConsistent,
  isAdminLeadTagPageConsistent,
  isAdminLeadTaskPageConsistent,
  isAdminLeadTaskReminderPageConsistent,
  isEngagementFailureOutcome,
  isReminderScheduleConsistentWithTask,
  mergeAdminLeadNotes,
  mergeAdminLeadTags,
  mergeAdminLeadTasks,
  mergeAdminLeadTaskReminders,
  normalizeCrmTagDisplayLabel,
  normalizeCrmTaskTitle,
  normalizeLeadNoteBody,
  taskReminderStatusLabels,
  type AdminLeadEngagementPageState,
} from "./lead-engagement-model";

interface AdminLeadEngagementPanelProps {
  readonly leadId: LeadId;
  readonly leadRecordStatus: "ACTIVE" | "ARCHIVED";
  readonly gateways: AdminLeadEngagementGateways | null;
  readonly detailRefreshing?: boolean;
  readonly externalMutationPending?: boolean;
  readonly onPendingChange?: (pending: boolean) => void;
}

interface EngagementPage<T> {
  readonly leadId: LeadId;
  readonly items: readonly T[];
  readonly nextCursor: AdminLeadEngagementCursor | null;
}

type EngagementReadGateway<P> = (
  query: {
    readonly leadId: LeadId;
    readonly cursor: AdminLeadEngagementCursor | null;
    readonly limit: 25;
  },
  context: AdminLeadEngagementReadContext,
) => Promise<P>;

interface EngagementPageController<T> {
  readonly state: AdminLeadEngagementPageState<T>;
  readonly retry: () => void;
  readonly refresh: () => Promise<boolean>;
  readonly loadMore: () => void;
  readonly replaceItems: (items: readonly T[]) => void;
}

interface EngagementFeedback {
  readonly tone: "positive" | "critical";
  readonly message: string;
  readonly refreshRequired: boolean;
}

type MutationKind = "tag" | "task" | "reminder" | "note";

function isEngagementPageBusy<T>(
  state: AdminLeadEngagementPageState<T>,
): boolean {
  return (
    state.kind === "loading" ||
    (state.kind === "ready" && (state.refreshing || state.loadingMore))
  );
}

function failedPageState<T>(
  current: AdminLeadEngagementPageState<T>,
  mode: "initial" | "refresh" | "load-more",
): AdminLeadEngagementPageState<T> {
  if (mode === "initial" || current.kind !== "ready") {
    return { kind: "error" };
  }
  return {
    ...current,
    refreshing: false,
    loadingMore: false,
    notice: mode === "refresh" ? "REFRESH_FAILED" : "LOAD_MORE_FAILED",
  };
}

function useEngagementPage<T, P extends EngagementPage<T>>({
  cleanerId,
  leadId,
  gateway,
  validate,
  merge,
}: {
  readonly cleanerId: string;
  readonly leadId: LeadId;
  readonly gateway: EngagementReadGateway<P> | null;
  readonly validate: (value: unknown, leadId: LeadId) => value is P;
  readonly merge: (
    current: readonly T[],
    incoming: readonly T[],
  ) => readonly T[] | null;
}): EngagementPageController<T> {
  const [state, setState] = useState<AdminLeadEngagementPageState<T>>(
    gateway ? { kind: "loading" } : { kind: "unavailable" },
  );
  const stateRef = useRef(state);
  stateRef.current = state;
  const sequence = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const commitState = useCallback(
    (
      next:
        | AdminLeadEngagementPageState<T>
        | ((
            current: AdminLeadEngagementPageState<T>,
          ) => AdminLeadEngagementPageState<T>),
    ) => {
      setState((current) => {
        const resolved = typeof next === "function" ? next(current) : next;
        stateRef.current = resolved;
        return resolved;
      });
    },
    [],
  );

  const request = useCallback(
    async (
      mode: "initial" | "refresh" | "load-more",
      cursor: AdminLeadEngagementCursor | null,
    ): Promise<boolean> => {
      if (!gateway) {
        commitState({ kind: "unavailable" });
        return true;
      }

      const requestId = sequence.current + 1;
      sequence.current = requestId;
      activeRequest.current?.abort();
      const controller = new AbortController();
      activeRequest.current = controller;
      const generation = captureProtectedStateGeneration();

      commitState((current) => {
        if (mode === "initial" || current.kind !== "ready") {
          return { kind: "loading" };
        }
        return {
          ...current,
          refreshing: mode === "refresh",
          loadingMore: mode === "load-more",
          notice: null,
        };
      });

      try {
        const page = await gateway(
          {
            leadId,
            cursor,
            limit: ADMIN_LEAD_ENGAGEMENT_PAGE_SIZE,
          },
          { signal: controller.signal },
        );
        if (
          controller.signal.aborted ||
          sequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(generation)
        ) {
          return false;
        }
        if (!validate(page, leadId)) {
          commitState((current) => failedPageState(current, mode));
          return false;
        }

        const current = stateRef.current;
        if (
          mode === "load-more" &&
          (current.kind !== "ready" ||
            cursor === null ||
            page.nextCursor === cursor ||
            (page.nextCursor !== null &&
              current.consumedCursors.includes(page.nextCursor)))
        ) {
          commitState(failedPageState(current, mode));
          return false;
        }

        const items =
          mode === "load-more" && current.kind === "ready"
            ? merge(current.items, page.items)
            : merge([], page.items);
        if (items === null) {
          commitState(failedPageState(current, mode));
          return false;
        }

        commitState({
          kind: "ready",
          items,
          nextCursor: page.nextCursor,
          consumedCursors:
            mode === "load-more" && current.kind === "ready" && cursor !== null
              ? [...current.consumedCursors, cursor]
              : [],
          refreshing: false,
          loadingMore: false,
          notice: null,
        });
        return true;
      } catch {
        if (
          controller.signal.aborted ||
          sequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(generation)
        ) {
          return false;
        }
        commitState((current) => failedPageState(current, mode));
        return false;
      } finally {
        if (activeRequest.current === controller) {
          activeRequest.current = null;
        }
      }
    },
    [commitState, gateway, leadId, merge, validate],
  );

  useEffect(() => {
    const unregister = registerProtectedStateCleaner(cleanerId, () => {
      sequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
      commitState(gateway ? { kind: "loading" } : { kind: "unavailable" });
    });
    if (gateway) {
      void request("initial", null);
    } else {
      commitState({ kind: "unavailable" });
    }
    return () => {
      unregister();
      sequence.current += 1;
      activeRequest.current?.abort();
      activeRequest.current = null;
    };
  }, [cleanerId, commitState, gateway, request]);

  return {
    state,
    retry: () =>
      void request(state.kind === "ready" ? "refresh" : "initial", null),
    refresh: () =>
      request(state.kind === "ready" ? "refresh" : "initial", null),
    loadMore: () => {
      if (state.kind === "ready" && state.nextCursor) {
        void request("load-more", state.nextCursor);
      }
    },
    replaceItems: (items) => {
      commitState((current) =>
        current.kind === "ready" ? { ...current, items } : current,
      );
    },
  };
}

function EngagementDate({ value }: { readonly value: string }) {
  const formatted = formatHawaiiEngagementDate(value);
  return formatted ? <time dateTime={value}>{formatted}</time> : "Unavailable";
}

function EngagementSection({
  id,
  title,
  description,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly children: ReactNode;
}) {
  const descriptionId = `${id}-description`;
  return (
    <section
      className="crm-lead-engagement__section"
      aria-labelledby={id}
      aria-describedby={descriptionId}
    >
      <div className="crm-lead-engagement__section-heading">
        <h4 id={id}>{title}</h4>
        <p id={descriptionId}>{description}</p>
      </div>
      {children}
    </section>
  );
}

function PagedState<T>({
  state,
  noun,
  onRetry,
  onLoadMore,
  disabled = false,
  children,
}: {
  readonly state: AdminLeadEngagementPageState<T>;
  readonly noun: string;
  readonly onRetry: () => void;
  readonly onLoadMore: () => void;
  readonly disabled?: boolean;
  readonly children: (items: readonly T[]) => ReactNode;
}) {
  if (state.kind === "unavailable") {
    return (
      <ContentState
        kind="warning"
        headingLevel={4}
        title={`${noun} are unavailable.`}
      >
        <p>
          The protected read service has not been connected. No sample or cached
          records are shown.
        </p>
      </ContentState>
    );
  }
  if (state.kind === "loading") {
    return (
      <ContentState
        kind="loading"
        headingLevel={4}
        title={`Loading ${noun.toLowerCase()}.`}
      />
    );
  }
  if (state.kind === "error") {
    return (
      <ContentState
        kind="error"
        headingLevel={4}
        title={`${noun} could not be loaded.`}
        actions={
          <button type="button" disabled={disabled} onClick={onRetry}>
            Try again
          </button>
        }
      >
        <p>No unconfirmed records are shown.</p>
      </ContentState>
    );
  }

  return (
    <>
      {state.items.length === 0 ? (
        <ContentState
          kind="empty"
          headingLevel={4}
          title={`No ${noun.toLowerCase()} are recorded.`}
        />
      ) : (
        children(state.items)
      )}
      {state.notice && (
        <ContentState
          kind="error"
          headingLevel={4}
          title={
            state.notice === "REFRESH_FAILED"
              ? `${noun} could not be refreshed.`
              : `More ${noun.toLowerCase()} could not be loaded.`
          }
          actions={
            <button
              type="button"
              disabled={disabled}
              onClick={state.notice === "REFRESH_FAILED" ? onRetry : onLoadMore}
            >
              Try again
            </button>
          }
        >
          <p>The last confirmed records remain visible.</p>
        </ContentState>
      )}
      {state.nextCursor && (
        <button
          type="button"
          className="crm-lead-detail__button crm-lead-detail__button--secondary"
          aria-disabled={
            disabled || state.loadingMore || state.refreshing
              ? "true"
              : undefined
          }
          onClick={() => {
            if (!disabled && !state.loadingMore && !state.refreshing) {
              onLoadMore();
            }
          }}
        >
          {state.loadingMore
            ? `Loading ${noun.toLowerCase()}`
            : `Load more ${noun.toLowerCase()}`}
        </button>
      )}
      {(state.loadingMore || state.refreshing) && (
        <p className="crm-lead-detail__refresh-status" role="status">
          {state.refreshing
            ? `Refreshing ${noun.toLowerCase()}.`
            : `Loading more ${noun.toLowerCase()}.`}{" "}
          The last confirmed records remain visible.
        </p>
      )}
    </>
  );
}

function ReadOnlyReason({
  archived,
  mutationAvailable,
}: {
  readonly archived: boolean;
  readonly mutationAvailable: boolean;
}) {
  if (!archived && mutationAvailable) return null;
  return (
    <p className="crm-lead-engagement__scope-note">
      {archived
        ? "This archived lead retains confirmed engagement records, but changes are disabled."
        : "The authorized mutation service has not been connected, so confirmed records are read-only."}
    </p>
  );
}

function createEngagementIdempotencyKey(): IdempotencyKey | null {
  try {
    const value = globalThis.crypto?.randomUUID?.();
    return value && safeIdempotencyKeyPattern.test(value)
      ? (value as IdempotencyKey)
      : null;
  } catch {
    return null;
  }
}

const safeIdempotencyKeyPattern = /^[A-Za-z0-9_-]{8,128}$/;

export function AdminLeadEngagementPanel({
  leadId,
  leadRecordStatus,
  gateways,
  detailRefreshing = false,
  externalMutationPending = false,
  onPendingChange,
}: AdminLeadEngagementPanelProps) {
  const archived = leadRecordStatus === "ARCHIVED";
  const tags = useEngagementPage({
    cleanerId: "crm-admin-lead-tags",
    leadId,
    gateway: gateways?.readTags ?? null,
    validate: isAdminLeadTagPageConsistent,
    merge: mergeAdminLeadTags,
  });
  const tasks = useEngagementPage({
    cleanerId: "crm-admin-lead-tasks",
    leadId,
    gateway: gateways?.readTasks ?? null,
    validate: isAdminLeadTaskPageConsistent,
    merge: mergeAdminLeadTasks,
  });
  const reminders = useEngagementPage({
    cleanerId: "crm-admin-lead-reminders",
    leadId,
    gateway: gateways?.readReminders ?? null,
    validate: isAdminLeadTaskReminderPageConsistent,
    merge: mergeAdminLeadTaskReminders,
  });
  const notes = useEngagementPage({
    cleanerId: "crm-admin-lead-notes",
    leadId,
    gateway: gateways?.readNotes ?? null,
    validate: isAdminLeadNotePageConsistent,
    merge: mergeAdminLeadNotes,
  });

  const [tagDraft, setTagDraft] = useState("");
  const [taskTitleDraft, setTaskTitleDraft] = useState("");
  const [taskDueDraft, setTaskDueDraft] = useState("");
  const [reminderTaskId, setReminderTaskId] = useState("");
  const [reminderAtDraft, setReminderAtDraft] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [fieldError, setFieldError] = useState<{
    readonly field: "tag" | "task-title" | "task-due" | "reminder" | "note";
    readonly copy: string;
  } | null>(null);
  const [feedback, setFeedback] = useState<EngagementFeedback | null>(null);
  const [pendingKind, setPendingKind] = useState<MutationKind | null>(null);
  const [refreshInProgress, setRefreshInProgress] = useState(false);
  const refreshInProgressRef = useRef(false);
  const mutationSequence = useRef(0);
  const activeMutation = useRef<AbortController | null>(null);
  const feedbackRef = useRef<HTMLParagraphElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const unregister = registerProtectedStateCleaner(
      "crm-admin-lead-engagement-mutation",
      () => {
        mutationSequence.current += 1;
        activeMutation.current?.abort();
        activeMutation.current = null;
        setPendingKind(null);
        refreshInProgressRef.current = false;
        setRefreshInProgress(false);
        onPendingChange?.(false);
        setTagDraft("");
        setTaskTitleDraft("");
        setTaskDueDraft("");
        setReminderTaskId("");
        setReminderAtDraft("");
        setNoteDraft("");
        setFieldError(null);
        setFeedback(null);
      },
    );
    return () => {
      unregister();
      mutationSequence.current += 1;
      activeMutation.current?.abort();
      activeMutation.current = null;
      setPendingKind(null);
      refreshInProgressRef.current = false;
      setRefreshInProgress(false);
      onPendingChange?.(false);
    };
  }, [onPendingChange]);

  useEffect(() => {
    if (feedback) feedbackRef.current?.focus();
  }, [feedback]);

  const requireRefresh = (message: string) => {
    setFeedback({ tone: "critical", message, refreshRequired: true });
  };

  const pageReadPending =
    isEngagementPageBusy(tags.state) ||
    isEngagementPageBusy(tasks.state) ||
    isEngagementPageBusy(reminders.state) ||
    isEngagementPageBusy(notes.state);

  const runMutation = async ({
    kind,
    execute,
    commit,
    successMessage,
    onConfirmed,
  }: {
    readonly kind: MutationKind;
    readonly execute: (
      signal: AbortSignal,
      idempotencyKey: IdempotencyKey,
    ) => Promise<unknown>;
    readonly commit: (outcome: unknown) => readonly unknown[] | null;
    readonly successMessage: string;
    readonly onConfirmed: (items: readonly unknown[]) => void;
  }) => {
    if (
      activeMutation.current ||
      pendingKind ||
      feedback?.refreshRequired ||
      archived ||
      detailRefreshing ||
      externalMutationPending ||
      pageReadPending ||
      refreshInProgressRef.current
    ) {
      return;
    }
    const idempotencyKey = createEngagementIdempotencyKey();
    if (!idempotencyKey) {
      requireRefresh(
        "The change could not be prepared safely. Refresh engagement before trying again.",
      );
      return;
    }

    const requestId = mutationSequence.current + 1;
    mutationSequence.current = requestId;
    const controller = new AbortController();
    activeMutation.current = controller;
    const generation = captureProtectedStateGeneration();
    setPendingKind(kind);
    onPendingChange?.(true);
    setFeedback(null);

    try {
      const outcome = await execute(controller.signal, idempotencyKey);
      if (
        controller.signal.aborted ||
        mutationSequence.current !== requestId ||
        !isProtectedStateGenerationCurrent(generation)
      ) {
        return;
      }
      if (isEngagementFailureOutcome(outcome)) {
        requireRefresh(
          outcome.kind === "conflict"
            ? "These engagement records changed elsewhere. Refresh before trying again."
            : "The change was not confirmed. Refresh before trying again.",
        );
        return;
      }
      const nextItems = commit(outcome);
      if (!nextItems) {
        requireRefresh(
          "The response could not be confirmed. Refresh engagement before trying again.",
        );
        return;
      }
      onConfirmed(nextItems);
      setFeedback({
        tone: "positive",
        message: successMessage,
        refreshRequired: false,
      });
    } catch {
      if (
        controller.signal.aborted ||
        mutationSequence.current !== requestId ||
        !isProtectedStateGenerationCurrent(generation)
      ) {
        return;
      }
      requireRefresh(
        "The change could not be confirmed. Refresh engagement before trying again.",
      );
    } finally {
      if (
        mutationSequence.current === requestId &&
        isProtectedStateGenerationCurrent(generation)
      ) {
        activeMutation.current = null;
        setPendingKind(null);
        onPendingChange?.(false);
      }
    }
  };

  const refreshAll = async () => {
    if (
      activeMutation.current ||
      externalMutationPending ||
      refreshInProgressRef.current
    ) {
      return;
    }
    const refreshWasRequired = feedback?.refreshRequired === true;
    const generation = captureProtectedStateGeneration();
    refreshInProgressRef.current = true;
    setRefreshInProgress(true);
    panelRef.current?.focus();
    if (!refreshWasRequired) setFeedback(null);
    setFieldError(null);
    const outcomes = await Promise.all([
      tags.refresh(),
      tasks.refresh(),
      reminders.refresh(),
      notes.refresh(),
    ]);
    if (!isProtectedStateGenerationCurrent(generation)) return;
    const succeeded = outcomes.every(Boolean);
    if (refreshWasRequired) {
      setFeedback(
        succeeded
          ? {
              tone: "positive",
              message:
                "Engagement records were refreshed. Changes are available again.",
              refreshRequired: false,
            }
          : {
              tone: "critical",
              message:
                "Not every engagement section refreshed successfully. Changes remain locked.",
              refreshRequired: true,
            },
      );
    }
    refreshInProgressRef.current = false;
    setRefreshInProgress(false);
  };

  if (!gateways) {
    return (
      <section
        className="crm-lead-engagement"
        aria-labelledby="crm-lead-engagement-title"
      >
        <h3 id="crm-lead-engagement-title">Engagement</h3>
        <ContentState
          kind="warning"
          headingLevel={4}
          title="Engagement tools are unavailable."
        >
          <p>
            The protected tags, tasks, reminders, and notes services have not
            been connected. No sample or cached records are shown.
          </p>
        </ContentState>
      </section>
    );
  }

  const controlsDisabled =
    pendingKind !== null ||
    feedback?.refreshRequired === true ||
    detailRefreshing ||
    externalMutationPending ||
    pageReadPending ||
    refreshInProgress;
  const openTasks =
    tasks.state.kind === "ready"
      ? tasks.state.items.filter((task) => task.status === "OPEN")
      : [];

  const submitTag = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const displayLabel = normalizeCrmTagDisplayLabel(tagDraft);
    if (!displayLabel) {
      setFieldError({
        field: "tag",
        copy: "Enter a short operational label of 1 to 32 characters.",
      });
      return;
    }
    const currentItems = tags.state.kind === "ready" ? tags.state.items : null;
    if (!gateways.mutateTag || !currentItems) return;
    const request: AdminLeadTagMutationRequest = {
      action: "ADD",
      leadId,
      displayLabel,
    };
    setFieldError(null);
    void runMutation({
      kind: "tag",
      execute: (signal, idempotencyKey) =>
        gateways.mutateTag!(request, { signal, idempotencyKey }),
      commit: (outcome) =>
        applyConfirmedAdminLeadTagMutation(currentItems, request, outcome),
      successMessage: "The tag was confirmed.",
      onConfirmed: (items) => {
        tags.replaceItems(items as readonly AdminLeadTag[]);
        setTagDraft("");
      },
    });
  };

  const removeTag = (item: AdminLeadTag) => {
    const currentItems = tags.state.kind === "ready" ? tags.state.items : null;
    if (!gateways.mutateTag || !currentItems) return;
    const request: AdminLeadTagMutationRequest = {
      action: "REMOVE",
      leadId,
      assignmentId: item.assignmentId,
      expectedRevision: item.revision,
      expectedUpdatedAt: item.updatedAt,
    };
    void runMutation({
      kind: "tag",
      execute: (signal, idempotencyKey) =>
        gateways.mutateTag!(request, { signal, idempotencyKey }),
      commit: (outcome) =>
        applyConfirmedAdminLeadTagMutation(currentItems, request, outcome),
      successMessage: "The tag removal was confirmed.",
      onConfirmed: (items) =>
        tags.replaceItems(items as readonly AdminLeadTag[]),
    });
  };

  const submitTask = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = normalizeCrmTaskTitle(taskTitleDraft);
    if (!title) {
      setFieldError({
        field: "task-title",
        copy: "Enter an operational task title of 1 to 120 characters.",
      });
      return;
    }
    const dueAt = hawaiiLocalDateTimeToCanonicalUtc(taskDueDraft);
    if (!dueAt) {
      setFieldError({
        field: "task-due",
        copy: "Enter a valid Hawaiʻi date and time.",
      });
      return;
    }
    const currentItems =
      tasks.state.kind === "ready" ? tasks.state.items : null;
    if (!gateways.mutateTask || !currentItems) return;
    const request: AdminLeadTaskMutationRequest = {
      action: "CREATE",
      leadId,
      title,
      dueAt,
    };
    setFieldError(null);
    void runMutation({
      kind: "task",
      execute: (signal, idempotencyKey) =>
        gateways.mutateTask!(request, { signal, idempotencyKey }),
      commit: (outcome) =>
        applyConfirmedAdminLeadTaskMutation(currentItems, request, outcome),
      successMessage: "The task was confirmed.",
      onConfirmed: (items) => {
        tasks.replaceItems(items as readonly AdminLeadTask[]);
        setTaskTitleDraft("");
        setTaskDueDraft("");
      },
    });
  };

  const closeTask = (item: AdminLeadTask, action: "COMPLETE" | "CANCEL") => {
    const currentItems =
      tasks.state.kind === "ready" ? tasks.state.items : null;
    if (!gateways.mutateTask || !currentItems) return;
    const request: AdminLeadTaskMutationRequest = {
      action,
      leadId,
      taskId: item.taskId,
      expectedRevision: item.revision,
      expectedUpdatedAt: item.updatedAt,
    };
    void runMutation({
      kind: "task",
      execute: (signal, idempotencyKey) =>
        gateways.mutateTask!(request, { signal, idempotencyKey }),
      commit: (outcome) =>
        applyConfirmedAdminLeadTaskMutation(currentItems, request, outcome),
      successMessage:
        action === "COMPLETE"
          ? "The task completion was confirmed."
          : "The task cancellation was confirmed.",
      onConfirmed: (items) =>
        tasks.replaceItems(items as readonly AdminLeadTask[]),
    });
  };

  const submitReminder = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const scheduledFor = hawaiiLocalDateTimeToCanonicalUtc(reminderAtDraft);
    const selectedTask = openTasks.find(
      (task) => task.taskId === reminderTaskId,
    );
    if (!selectedTask || !scheduledFor) {
      setFieldError({
        field: "reminder",
        copy: "Choose an open task and a valid Hawaiʻi date and time.",
      });
      return;
    }
    if (
      !isReminderScheduleConsistentWithTask(
        selectedTask,
        scheduledFor,
        new Date(),
      )
    ) {
      setFieldError({
        field: "reminder",
        copy: "Choose a future marker after task creation and no later than the task due time.",
      });
      return;
    }
    const currentReminders =
      reminders.state.kind === "ready" ? reminders.state.items : null;
    const currentTasks =
      tasks.state.kind === "ready" ? tasks.state.items : null;
    if (!gateways.mutateReminder || !currentReminders || !currentTasks) {
      return;
    }
    const request: AdminLeadTaskReminderMutationRequest = {
      action: "SCHEDULE",
      leadId,
      taskId: selectedTask.taskId,
      scheduledFor,
    };
    setFieldError(null);
    void runMutation({
      kind: "reminder",
      execute: (signal, idempotencyKey) =>
        gateways.mutateReminder!(request, { signal, idempotencyKey }),
      commit: (outcome) =>
        applyConfirmedAdminLeadReminderMutation(
          currentReminders,
          currentTasks,
          request,
          outcome,
        ),
      successMessage:
        "The reminder marker was scheduled. This does not confirm delivery.",
      onConfirmed: (items) => {
        reminders.replaceItems(items as readonly AdminLeadTaskReminder[]);
        setReminderAtDraft("");
      },
    });
  };

  const cancelReminder = (item: AdminLeadTaskReminder) => {
    const currentReminders =
      reminders.state.kind === "ready" ? reminders.state.items : null;
    const currentTasks = tasks.state.kind === "ready" ? tasks.state.items : [];
    if (!gateways.mutateReminder || !currentReminders) {
      return;
    }
    const request: AdminLeadTaskReminderMutationRequest = {
      action: "CANCEL",
      leadId,
      reminderId: item.reminderId,
      expectedRevision: item.revision,
      expectedUpdatedAt: item.updatedAt,
    };
    void runMutation({
      kind: "reminder",
      execute: (signal, idempotencyKey) =>
        gateways.mutateReminder!(request, { signal, idempotencyKey }),
      commit: (outcome) =>
        applyConfirmedAdminLeadReminderMutation(
          currentReminders,
          currentTasks,
          request,
          outcome,
        ),
      successMessage: "The reminder cancellation was confirmed.",
      onConfirmed: (items) =>
        reminders.replaceItems(items as readonly AdminLeadTaskReminder[]),
    });
  };

  const submitNote = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const bodyText = normalizeLeadNoteBody(noteDraft);
    if (!bodyText) {
      setFieldError({
        field: "note",
        copy: "Enter an operational note of 1 to 4,000 characters.",
      });
      return;
    }
    const currentItems =
      notes.state.kind === "ready" ? notes.state.items : null;
    if (!gateways.createNote || !currentItems) return;
    const request: AdminLeadNoteCreateRequest = {
      leadId,
      bodyText,
      supersedesNoteId: null,
    };
    setFieldError(null);
    void runMutation({
      kind: "note",
      execute: (signal, idempotencyKey) =>
        gateways.createNote!(request, { signal, idempotencyKey }),
      commit: (outcome) =>
        applyConfirmedAdminLeadNoteCreation(currentItems, request, outcome),
      successMessage: "The note was confirmed.",
      onConfirmed: (items) => {
        notes.replaceItems(items as readonly AdminLeadNote[]);
        setNoteDraft("");
      },
    });
  };

  return (
    <section
      ref={panelRef}
      className="crm-lead-engagement"
      aria-labelledby="crm-lead-engagement-title"
      aria-describedby="crm-lead-engagement-scope"
      aria-busy={
        pendingKind || pageReadPending || refreshInProgress ? "true" : undefined
      }
      tabIndex={-1}
    >
      <div className="crm-lead-engagement__heading">
        <div>
          <h3 id="crm-lead-engagement-title">Engagement</h3>
          <p id="crm-lead-engagement-scope">
            Protected operational tags, follow-up tasks, reminder markers, and
            notes. Do not enter diagnoses, medications, clinical history, or
            emergency information.
          </p>
        </div>
        <button
          type="button"
          className="crm-lead-detail__button crm-lead-detail__button--secondary"
          disabled={
            pendingKind !== null ||
            detailRefreshing ||
            externalMutationPending ||
            pageReadPending ||
            refreshInProgress
          }
          onClick={() => void refreshAll()}
        >
          Refresh engagement
        </button>
      </div>

      {archived && (
        <ContentState
          kind="warning"
          headingLevel={4}
          title="Archived lead engagement is read-only."
        >
          <p>
            Confirmed retained records remain available to authorized staff.
          </p>
        </ContentState>
      )}

      {feedback && (
        <p
          ref={feedbackRef}
          className={`crm-lead-engagement__feedback crm-lead-engagement__feedback--${feedback.tone}`}
          role={feedback.tone === "critical" ? "alert" : "status"}
          aria-live={feedback.tone === "critical" ? "assertive" : "polite"}
          aria-atomic="true"
          tabIndex={-1}
        >
          {feedback.message}
          {feedback.refreshRequired && (
            <button
              type="button"
              className="crm-lead-detail__button crm-lead-detail__button--secondary"
              onClick={() => void refreshAll()}
            >
              Refresh engagement
            </button>
          )}
        </p>
      )}

      <div className="crm-lead-engagement__grid">
        <EngagementSection
          id="crm-lead-tags-title"
          title="Tags"
          description="Short operational labels attached to this lead."
        >
          <PagedState
            state={tags.state}
            noun="Tags"
            onRetry={tags.retry}
            onLoadMore={tags.loadMore}
            disabled={controlsDisabled}
          >
            {(items) => (
              <ul className="crm-lead-engagement__tag-list">
                {items.map((tag) => (
                  <li key={tag.assignmentId}>
                    <StatusPill>{tag.displayLabel}</StatusPill>
                    {!archived && gateways.mutateTag && (
                      <button
                        type="button"
                        className="crm-lead-engagement__compact-button"
                        disabled={controlsDisabled}
                        aria-label={`Remove tag ${tag.displayLabel}`}
                        onClick={() => removeTag(tag)}
                      >
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </PagedState>
          <ReadOnlyReason
            archived={archived}
            mutationAvailable={gateways.mutateTag !== null}
          />
          {!archived && gateways.mutateTag && tags.state.kind === "ready" && (
            <form className="crm-lead-engagement__form" onSubmit={submitTag}>
              <FormField
                id="crm-lead-tag-label"
                label="Operational tag"
                hint="1–32 characters. Avoid clinical or sensitive details."
                error={
                  fieldError?.field === "tag" ? fieldError.copy : undefined
                }
                required
              >
                {(props) => (
                  <TextInput
                    {...props}
                    value={tagDraft}
                    maxLength={32}
                    disabled={controlsDisabled}
                    onChange={(event) => {
                      setTagDraft(event.currentTarget.value);
                      setFieldError(null);
                    }}
                  />
                )}
              </FormField>
              <FormActions align="start">
                <button
                  type="submit"
                  className="crm-lead-detail__button"
                  disabled={controlsDisabled}
                >
                  {pendingKind === "tag" ? "Confirming tag" : "Add tag"}
                </button>
              </FormActions>
            </form>
          )}
        </EngagementSection>

        <EngagementSection
          id="crm-lead-tasks-title"
          title="Tasks"
          description="Operational follow-up work, shown in Hawaiʻi time."
        >
          <PagedState
            state={tasks.state}
            noun="Tasks"
            onRetry={tasks.retry}
            onLoadMore={tasks.loadMore}
            disabled={controlsDisabled}
          >
            {(items) => (
              <ul className="crm-lead-engagement__record-list">
                {items.map((task) => (
                  <li key={task.taskId}>
                    <div className="crm-lead-engagement__record-heading">
                      <strong>{task.title}</strong>
                      <StatusPill
                        tone={
                          task.status === "OPEN"
                            ? "warning"
                            : task.status === "COMPLETED"
                              ? "positive"
                              : "neutral"
                        }
                      >
                        {crmTaskStatusLabels[task.status]}
                      </StatusPill>
                    </div>
                    <p>
                      Due <EngagementDate value={task.dueAt} />
                    </p>
                    {!archived &&
                      gateways.mutateTask &&
                      task.status === "OPEN" && (
                        <div className="crm-lead-engagement__record-actions">
                          <button
                            type="button"
                            className="crm-lead-engagement__compact-button"
                            disabled={controlsDisabled}
                            onClick={() => closeTask(task, "COMPLETE")}
                          >
                            Complete
                          </button>
                          <button
                            type="button"
                            className="crm-lead-engagement__compact-button"
                            disabled={controlsDisabled}
                            onClick={() => closeTask(task, "CANCEL")}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                  </li>
                ))}
              </ul>
            )}
          </PagedState>
          <ReadOnlyReason
            archived={archived}
            mutationAvailable={gateways.mutateTask !== null}
          />
          {!archived && gateways.mutateTask && tasks.state.kind === "ready" && (
            <form className="crm-lead-engagement__form" onSubmit={submitTask}>
              <FormField
                id="crm-lead-task-title"
                label="Task title"
                hint="Keep it operational and wellness-only."
                error={
                  fieldError?.field === "task-title"
                    ? fieldError.copy
                    : undefined
                }
                required
              >
                {(props) => (
                  <TextInput
                    {...props}
                    value={taskTitleDraft}
                    maxLength={120}
                    disabled={controlsDisabled}
                    onChange={(event) => {
                      setTaskTitleDraft(event.currentTarget.value);
                      setFieldError(null);
                    }}
                  />
                )}
              </FormField>
              <FormField
                id="crm-lead-task-due"
                label="Due in Hawaiʻi"
                hint="Date and time are interpreted as Hawaiʻi Standard Time."
                error={
                  fieldError?.field === "task-due" ? fieldError.copy : undefined
                }
                required
              >
                {(props) => (
                  <TextInput
                    {...props}
                    type="datetime-local"
                    value={taskDueDraft}
                    disabled={controlsDisabled}
                    onChange={(event) => {
                      setTaskDueDraft(event.currentTarget.value);
                      setFieldError(null);
                    }}
                  />
                )}
              </FormField>
              <FormActions align="start">
                <button
                  type="submit"
                  className="crm-lead-detail__button"
                  disabled={controlsDisabled}
                >
                  {pendingKind === "task" ? "Confirming task" : "Create task"}
                </button>
              </FormActions>
            </form>
          )}
        </EngagementSection>

        <EngagementSection
          id="crm-lead-reminders-title"
          title="Reminder markers"
          description="Scheduled follow-up markers only; this view does not claim notification delivery."
        >
          <PagedState
            state={reminders.state}
            noun="Reminder markers"
            onRetry={reminders.retry}
            onLoadMore={reminders.loadMore}
            disabled={controlsDisabled}
          >
            {(items) => (
              <ul className="crm-lead-engagement__record-list">
                {items.map((reminder) => {
                  const task = openTasks.find(
                    (candidate) => candidate.taskId === reminder.taskId,
                  );
                  return (
                    <li key={reminder.reminderId}>
                      <div className="crm-lead-engagement__record-heading">
                        <strong>{task?.title ?? "Task unavailable"}</strong>
                        <StatusPill
                          tone={
                            reminder.status === "PENDING"
                              ? "warning"
                              : "neutral"
                          }
                        >
                          {taskReminderStatusLabels[reminder.status]}
                        </StatusPill>
                      </div>
                      <p>
                        Marker time{" "}
                        <EngagementDate value={reminder.scheduledFor} />
                      </p>
                      {!archived &&
                        gateways.mutateReminder &&
                        reminder.status === "PENDING" && (
                          <button
                            type="button"
                            className="crm-lead-engagement__compact-button"
                            disabled={controlsDisabled}
                            onClick={() => cancelReminder(reminder)}
                          >
                            Cancel marker
                          </button>
                        )}
                    </li>
                  );
                })}
              </ul>
            )}
          </PagedState>
          <ReadOnlyReason
            archived={archived}
            mutationAvailable={gateways.mutateReminder !== null}
          />
          {!archived &&
            gateways.mutateReminder &&
            reminders.state.kind === "ready" &&
            tasks.state.kind === "ready" && (
              <form
                className="crm-lead-engagement__form"
                onSubmit={submitReminder}
              >
                <FormField
                  id="crm-lead-reminder-task"
                  label="Open task"
                  hint="The marker stays linked to one confirmed open task."
                  error={
                    fieldError?.field === "reminder"
                      ? fieldError.copy
                      : undefined
                  }
                  required
                >
                  {(props) => (
                    <Select
                      {...props}
                      value={reminderTaskId}
                      disabled={controlsDisabled || openTasks.length === 0}
                      onChange={(event) => {
                        setReminderTaskId(event.currentTarget.value);
                        setFieldError(null);
                      }}
                    >
                      <option value="">Choose an open task</option>
                      {openTasks.map((task) => (
                        <option key={task.taskId} value={task.taskId}>
                          {task.title}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormField>
                <FormField
                  id="crm-lead-reminder-at"
                  label="Marker time in Hawaiʻi"
                  hint="No email, text, push, or other delivery is promised."
                  error={
                    fieldError?.field === "reminder"
                      ? fieldError.copy
                      : undefined
                  }
                  required
                >
                  {(props) => (
                    <TextInput
                      {...props}
                      type="datetime-local"
                      value={reminderAtDraft}
                      disabled={controlsDisabled || openTasks.length === 0}
                      onChange={(event) => {
                        setReminderAtDraft(event.currentTarget.value);
                        setFieldError(null);
                      }}
                    />
                  )}
                </FormField>
                <FormActions align="start">
                  <button
                    type="submit"
                    className="crm-lead-detail__button"
                    disabled={controlsDisabled || openTasks.length === 0}
                  >
                    {pendingKind === "reminder"
                      ? "Confirming marker"
                      : "Schedule marker"}
                  </button>
                </FormActions>
              </form>
            )}
        </EngagementSection>

        <EngagementSection
          id="crm-lead-notes-title"
          title="Notes"
          description="Append-only operational context kept separate from the recorded CRM timeline."
        >
          <PagedState
            state={notes.state}
            noun="Notes"
            onRetry={notes.retry}
            onLoadMore={notes.loadMore}
            disabled={controlsDisabled}
          >
            {(items) => (
              <ol className="crm-lead-engagement__note-list">
                {items.map((note) => (
                  <li key={note.noteId}>
                    <EngagementDate value={note.createdAt} />
                    <p>{note.bodyText}</p>
                  </li>
                ))}
              </ol>
            )}
          </PagedState>
          <ReadOnlyReason
            archived={archived}
            mutationAvailable={gateways.createNote !== null}
          />
          {!archived && gateways.createNote && notes.state.kind === "ready" && (
            <form className="crm-lead-engagement__form" onSubmit={submitNote}>
              <FormField
                id="crm-lead-note-body"
                label="Operational note"
                hint="Wellness-only. Do not enter diagnoses, medications, clinical history, or emergency information."
                error={
                  fieldError?.field === "note" ? fieldError.copy : undefined
                }
                required
              >
                {(props) => (
                  <TextArea
                    {...props}
                    value={noteDraft}
                    maxLength={4000}
                    disabled={controlsDisabled}
                    onChange={(event) => {
                      setNoteDraft(event.currentTarget.value);
                      setFieldError(null);
                    }}
                  />
                )}
              </FormField>
              <FormActions align="start">
                <button
                  type="submit"
                  className="crm-lead-detail__button"
                  disabled={controlsDisabled}
                >
                  {pendingKind === "note" ? "Confirming note" : "Add note"}
                </button>
              </FormActions>
            </form>
          )}
        </EngagementSection>
      </div>
    </section>
  );
}
